import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { Subject } from 'rxjs';
import { BCSaaSOrganizationPersonRoleEntity, BCSaaSOrganizationRoleEntity } from '@bluecypress/bcsaas-entities';
import { MJRoleEntity } from '@memberjunction/core-entities';
import { mjBizAppsCommonOrganizationEntity, mjBizAppsCommonPersonEntity, mjBizAppsCommonRelationshipEntity } from '@mj-biz-apps/common-entities';
import { BCMembershipService } from '@bluecypress/bcsaas-entities-extended';
import { BC_ROLE_LEVELS } from '@bluecypress/bcsaas-core';
import { IzzyMetadataEngine } from 'mj_generatedentities';
import { IAdminPanel, AdminPanelStatus, DEFAULT_ADMIN_PANEL_STATUS } from '../admin-panel.interface';

// Post-BAC membership model:
//   - BAC Relationship (Status: Active | Ended | Inactive) is the source of truth for whether a
//     membership is active.
//   - __mj.Role is the role *catalog* (Name, Description) — the dropdown source.
//     Filter to canonical org roles: 'BCSaaS Organization Owner | Admin | Member | Read Only'.
//   - BC: Relationship Roles is a *bridge* table (1 row per Relationship) mapping the relationship
//     to the chosen __mj.Role and capturing Level. NOT a catalog.
//   - BC: Organization Person Roles is the denormalized junction read-side
//     (OrganizationID, PersonID, RelationshipID, RoleID, Level).
//     RoleID points at __mj.Role.ID directly.
//   - "Pending" memberships live in BC: Invitations.
//   - "Revoked" is Relationship.Status === 'Ended', set via BCMembershipService.RemoveMembership.

/**
 * Maps a canonical __mj.Role.Name to the BC role level constant. The catalog
 * uses the prefix 'BCSaaS Organization '; we strip it and look up the level
 * via BC_ROLE_LEVELS. Returns 0 for unrecognized names — caller should
 * surface a UI error in that case.
 */
const BC_ORG_ROLE_PREFIX = 'BCSaaS Organization ';

/**
 * Strips the 'BCSaaS Organization ' prefix from canonical __mj.Role.Name values
 * so the dropdown/badge shows just "Owner"/"Admin"/"Member"/"Read Only".
 * Display-only — the underlying row name in __mj.Role is unchanged.
 */
function shortRoleName(name: string | undefined | null): string {
  if (!name) return '';
  return name.startsWith(BC_ORG_ROLE_PREFIX) ? name.slice(BC_ORG_ROLE_PREFIX.length) : name;
}

function levelForRoleName(name: string | undefined | null): number {
  if (!name) return 0;
  const bare = shortRoleName(name);
  const key = bare.replace(/\s+/g, '_').toUpperCase() as keyof typeof BC_ROLE_LEVELS;
  return BC_ROLE_LEVELS[key] ?? 0;
}

interface MemberDisplay {
  member: BCSaaSOrganizationPersonRoleEntity;
  contact: mjBizAppsCommonPersonEntity | null;
  role: MJRoleEntity | null;
  isActive: boolean;
  isEditing: boolean;
  editForm: MemberEditForm;
}

interface MemberEditForm {
  roleId: string;
}

interface CreateForm {
  email: string;
  firstName: string;
  lastName: string;
  roleId: string;
}

type LookupState = 'idle' | 'searching' | 'found' | 'not_found' | 'already_member';

@Component({
  standalone: false,
  selector: 'izzy-organization-members',
  templateUrl: './organization-members.component.html',
  styleUrls: ['./organization-members.component.css']
})
export class OrganizationMembersComponent implements OnInit, OnDestroy, OnChanges, IAdminPanel {
  @Input() organizationId: string | undefined;
  @Input() initialState: any = null;
  @Output() stateChange = new EventEmitter<any>();
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();
  @Output() interaction = new EventEmitter<any>();

  constructor() {}

  protected members: BCSaaSOrganizationPersonRoleEntity[] = [];
  protected memberDisplays: MemberDisplay[] = [];
  protected contacts: mjBizAppsCommonPersonEntity[] = [];
  protected roles: MJRoleEntity[] = [];
  // Maps __mj.Role.ID -> BC: Organization Roles.ID, so the dropdown's Role.ID
  // selection can be translated to the OrganizationRole.ID that CreateMembership requires.
  private roleIdToOrgRoleId = new Map<string, string>();
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected selectedOrganization: mjBizAppsCommonOrganizationEntity | null = null;

  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected errorMessage: string = '';
  protected successMessage: string = '';
  protected searchQuery: string = '';
  // 'all' includes ended memberships; 'active' filters to currently-active only.
  protected statusFilter: 'all' | 'active' = 'active';

  protected isCreating: boolean = false;
  protected createForm: CreateForm = this.getEmptyCreateForm();

  // Email lookup state
  protected lookupState: LookupState = 'idle';
  protected foundContact: mjBizAppsCommonPersonEntity | null = null;
  private lookupTimeout: any = null;

  /** Exposed for template binding — strips the 'BCSaaS Organization ' prefix for display. */
  protected shortRoleName = shortRoleName;

  protected confirmDialogVisible: boolean = false;
  protected confirmDialogTitle: string = '';
  protected confirmDialogMessage: string = '';
  protected confirmDialogAction: (() => Promise<void>) | null = null;

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.lookupTimeout) {
      clearTimeout(this.lookupTimeout);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['organizationId'] && !changes['organizationId'].firstChange) {
      this.loadData();
    }
  }

  private getEmptyCreateForm(): CreateForm {
    return {
      email: '',
      firstName: '',
      lastName: '',
      roleId: ''
    };
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      // Load organizations
      if (!this.organizationId) {
        const rvOrgs = new RunView();
        const orgsResult = await rvOrgs.RunView<mjBizAppsCommonOrganizationEntity>({
          EntityName: 'MJ_BizApps_Common: Organizations',
          OrderBy: 'Name ASC'
        });
        this.organizations = orgsResult.Results;
        if (this.organizations.length > 0) {
          this.selectedOrganization = this.organizations[0];
        }
      } else {
        const rvOrg = new RunView();
        const orgResult = await rvOrg.RunView<mjBizAppsCommonOrganizationEntity>({
          EntityName: 'MJ_BizApps_Common: Organizations',
          ExtraFilter: `ID = '${this.organizationId}'`
        });
        if (orgResult.Results.length > 0) {
          this.selectedOrganization = orgResult.Results[0];
          this.organizations = [this.selectedOrganization];
        }
      }

      // Load the canonical role catalog from __mj.Role, filtered to BC org roles.
      // OrganizationPersonRole.RoleID and RelationshipRole.RoleID both point at __mj.Role.ID.
      // This catalog drives the dropdown/badge display (keyed on __mj.Role.ID).
      const rvRoles = new RunView();
      const rolesResult = await rvRoles.RunView<MJRoleEntity>({
        EntityName: 'MJ: Roles',
        ExtraFilter: `Name LIKE 'BCSaaS Organization %'`,
        OrderBy: 'Name'
      });
      this.roles = rolesResult.Results;

      // BCMembershipService.CreateMembership expects an OrganizationRole.ID
      // (PK of BC: Organization Roles), NOT an __mj.Role.ID. Build a map from
      // __mj.Role.ID -> OrganizationRole.ID so we can translate the dropdown
      // selection (a Role.ID) to the OrganizationRole.ID at create time.
      const rvOrgRoles = new RunView();
      const orgRolesResult = await rvOrgRoles.RunView<BCSaaSOrganizationRoleEntity>({
        EntityName: 'BC: Organization Roles'
      });
      this.roleIdToOrgRoleId = new Map<string, string>();
      for (const orgRole of orgRolesResult.Results) {
        if (orgRole.RoleID) {
          this.roleIdToOrgRoleId.set(orgRole.RoleID, orgRole.ID);
        }
      }

      if (this.selectedOrganization) {
        await this.loadMembers();
      }

      this.statusChange.emit(DEFAULT_ADMIN_PANEL_STATUS);
    } catch (error) {
      console.error('Error loading data:', error);
      this.errorMessage = 'Failed to load data.';
    } finally {
      this.isLoading = false;
    }
  }

  protected async loadMembers(): Promise<void> {
    if (!this.selectedOrganization) return;

    try {
      const rv = new RunView();
      const result = await rv.RunView<BCSaaSOrganizationPersonRoleEntity>({
        EntityName: 'BC: Organization Person Roles',
        ExtraFilter: `OrganizationID = '${this.selectedOrganization.ID}'`,
        OrderBy: '__mj_CreatedAt DESC',
        ResultType: 'entity_object'
      });
      this.members = result.Results;

      // Load the Persons referenced by these memberships
      if (this.members.length > 0) {
        const personIds = this.members.map(m => `'${m.PersonID}'`).join(',');
        const rvContacts = new RunView();
        const contactsResult = await rvContacts.RunView<mjBizAppsCommonPersonEntity>({
          EntityName: 'MJ_BizApps_Common: People',
          ExtraFilter: `ID IN (${personIds})`
        });
        this.contacts = contactsResult.Results;
      } else {
        this.contacts = [];
      }

      this.memberDisplays = this.members.map(m => this.createMemberDisplay(m));
    } catch (error) {
      console.error('Error loading members:', error);
      this.errorMessage = 'Failed to load members.';
    }
  }

  private createMemberDisplay(member: BCSaaSOrganizationPersonRoleEntity): MemberDisplay {
    const contact = this.contacts.find(c => c.ID === member.PersonID) || null;
    const role = this.roles.find(r => r.ID === member.RoleID) || null;
    return {
      member,
      contact,
      role,
      isActive: IzzyMetadataEngine.Instance.IsActiveMembership(member),
      isEditing: false,
      editForm: {
        roleId: member.RoleID || ''
      }
    };
  }

  async onOrganizationChange(): Promise<void> {
    this.isLoading = true;
    await this.loadMembers();
    this.isLoading = false;
  }

  get filteredMembers(): MemberDisplay[] {
    return this.memberDisplays.filter(md => {
      const contactName = md.contact ? `${md.contact.FirstName} ${md.contact.LastName}`.toLowerCase() : '';
      const contactEmail = md.contact ? md.contact.Email.toLowerCase() : '';
      const matchesSearch = !this.searchQuery.trim() ||
        contactName.includes(this.searchQuery.toLowerCase()) ||
        contactEmail.includes(this.searchQuery.toLowerCase());
      const matchesStatus = this.statusFilter === 'all' || md.isActive;
      return matchesSearch && matchesStatus;
    });
  }

  // Email lookup with debounce
  onEmailInput(): void {
    if (this.lookupTimeout) {
      clearTimeout(this.lookupTimeout);
    }

    const email = this.createForm.email.trim().toLowerCase();
    if (!email || !this.isValidEmail(email)) {
      this.lookupState = 'idle';
      this.foundContact = null;
      return;
    }

    this.lookupState = 'searching';
    this.lookupTimeout = setTimeout(() => {
      this.lookupContactByEmail(email);
    }, 500);
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  private async lookupContactByEmail(email: string): Promise<void> {
    try {
      const rv = new RunView();
      const result = await rv.RunView<mjBizAppsCommonPersonEntity>({
        EntityName: 'MJ_BizApps_Common: People',
        ExtraFilter: `Email = '${email.replace(/'/g, "''")}'`
      });

      if (result.Results.length > 0) {
        const contact = result.Results[0];
        const isMember = this.members.some(m => m.PersonID === contact.ID);
        if (isMember) {
          this.lookupState = 'already_member';
          this.foundContact = contact;
        } else {
          this.lookupState = 'found';
          this.foundContact = contact;
        }
      } else {
        this.lookupState = 'not_found';
        this.foundContact = null;
      }
    } catch (error) {
      console.error('Error looking up contact:', error);
      this.lookupState = 'not_found';
      this.foundContact = null;
    }
  }

  startCreate(): void {
    this.isCreating = true;
    this.createForm = this.getEmptyCreateForm();
    this.lookupState = 'idle';
    this.foundContact = null;
    if (this.roles.length > 0) {
      this.createForm.roleId = this.roles[0].ID;
    }
  }

  cancelCreate(): void {
    this.isCreating = false;
    this.createForm = this.getEmptyCreateForm();
    this.lookupState = 'idle';
    this.foundContact = null;
  }

  async saveCreate(): Promise<void> {
    if (!this.selectedOrganization) {
      this.errorMessage = 'No organization selected.';
      return;
    }

    const email = this.createForm.email.trim().toLowerCase();
    if (!email || !this.isValidEmail(email)) {
      this.errorMessage = 'Please enter a valid email address.';
      return;
    }

    if (this.lookupState === 'already_member') {
      this.errorMessage = 'This person is already a member of this organization.';
      return;
    }

    if (this.lookupState === 'not_found') {
      if (!this.createForm.firstName.trim() || !this.createForm.lastName.trim()) {
        this.errorMessage = 'First name and last name are required for new contacts.';
        return;
      }
    }

    if (!this.createForm.roleId) {
      this.errorMessage = 'A role is required.';
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    try {
      let personId: string;

      if (this.lookupState === 'found' && this.foundContact) {
        personId = this.foundContact.ID;
      } else {
        // Create new Person
        const md = new Metadata();
        const newContact = await md.GetEntityObject<mjBizAppsCommonPersonEntity>('MJ_BizApps_Common: People');
        newContact.Email = email;
        newContact.FirstName = this.createForm.firstName.trim();
        newContact.LastName = this.createForm.lastName.trim();
        newContact.Status = 'Active';
        await newContact.Save();
        personId = newContact.ID;
      }

      // The dropdown is keyed on __mj.Role.ID, but CreateMembership requires the
      // OrganizationRole.ID (PK of BC: Organization Roles). Translate before calling.
      const organizationRoleId = this.roleIdToOrgRoleId.get(this.createForm.roleId);
      if (!organizationRoleId) {
        this.errorMessage = 'Selected role is not a valid organization role.';
        return;
      }

      // v1.3.1: Level is sourced from BC: Organization Roles.Level (server-side),
      // not passed by the caller. CreateMembership signature is
      // (personID, orgID, organizationRoleID, contextUser, isSystem?).
      await BCMembershipService.CreateMembership(
        personId,
        this.selectedOrganization.ID,
        organizationRoleId,
        Metadata.Provider.CurrentUser
      );

      await this.loadMembers();
      this.isCreating = false;
      this.lookupState = 'idle';
      this.foundContact = null;
      this.showSuccess('Member added successfully');
    } catch (error) {
      console.error('Error adding member:', error);
      this.errorMessage = 'Failed to add member.';
    } finally {
      this.isSaving = false;
    }
  }

  startEdit(display: MemberDisplay): void {
    display.isEditing = true;
    display.editForm = {
      roleId: display.member.RoleID || ''
    };
  }

  cancelEdit(display: MemberDisplay): void {
    display.isEditing = false;
  }

  async saveEdit(display: MemberDisplay): Promise<void> {
    this.isSaving = true;
    try {
      // v1.4: OrganizationPersonRole.RoleID is read-only; role changes must go through
      // BCMembershipService (which enforces level sourcing, owner protection, and
      // membership reconciliation). UpdateMembership keys on the Relationship and takes an
      // OrganizationRole.ID — translate the dropdown's Role.ID via roleIdToOrgRoleId, same
      // as the add path (CreateMembership).
      const newOrgRoleId = display.editForm.roleId
        ? this.roleIdToOrgRoleId.get(display.editForm.roleId)
        : undefined;
      if (!newOrgRoleId) {
        this.errorMessage = 'Selected role is not a valid organization role.';
        return;
      }

      await BCMembershipService.UpdateMembership(
        display.member.RelationshipID,
        newOrgRoleId,
        Metadata.Provider.CurrentUser
      );

      await this.loadMembers();
      display.isEditing = false;
      this.showSuccess('Member updated');
    } catch (error) {
      console.error('Error updating member:', error);
      this.errorMessage = 'Failed to update member.';
    } finally {
      this.isSaving = false;
    }
  }

  confirmRemove(display: MemberDisplay): void {
    const contactName = display.contact ? `${display.contact.FirstName} ${display.contact.LastName}` : 'this member';
    this.showConfirmDialog(
      'Remove Member',
      `Are you sure you want to remove ${contactName} from this organization?`,
      async () => {
        this.isSaving = true;
        try {
          await BCMembershipService.RemoveMembership(
            display.member.RelationshipID,
            Metadata.Provider.CurrentUser
          );
          await this.loadMembers();
          this.showSuccess('Member removed');
        } catch (error) {
          console.error('Error removing member:', error);
          this.errorMessage = 'Failed to remove member.';
        } finally {
          this.isSaving = false;
        }
      }
    );
  }

  getInitials(firstName: string, lastName: string): string {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  }

  protected showConfirmDialog(title: string, message: string, action: () => Promise<void>): void {
    this.confirmDialogTitle = title;
    this.confirmDialogMessage = message;
    this.confirmDialogAction = action;
    this.confirmDialogVisible = true;
  }

  protected async confirmDialogYes(): Promise<void> {
    if (this.confirmDialogAction) {
      await this.confirmDialogAction();
    }
    this.closeConfirmDialog();
  }

  protected closeConfirmDialog(): void {
    this.confirmDialogVisible = false;
    this.confirmDialogAction = null;
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }
}

export function LoadOrganizationMembersComponent(): void {}
